'use client';

import { useState, useMemo, useEffect } from 'react';
import { Box, VStack, HStack } from '@terra/ui-styled-system';
import { Text, Tree, type TreeNode } from '@terra/ui-design-system';
import { File01Icon, Folder01Icon } from '@terra/ui-icons';
import type { EndpointFile } from '@terra/shared-contracts-git';

// Use the proper type from the schema
type FileNode = EndpointFile;
interface FileTreeProps {
  files: FileNode[];
  selectedFileId?: string;
  onFileSelect: (file: FileNode) => void;
}
// Build flat array of nodes from file list for Tree component
const buildFileTree = (files: FileNode[]): TreeNode<FileNode>[] => {
  const nodes: TreeNode<FileNode>[] = [];
  const pathMap = new Map<string, TreeNode<FileNode>>();

  files.forEach((file) => {
    const pathParts = file.filePath.split('/');
    let currentPath = '';
    let parentNode: TreeNode<FileNode> | undefined;

    pathParts.forEach((part: string, index: number) => {
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      const isLast = index === pathParts.length - 1;

      if (!pathMap.has(currentPath)) {
        const node: TreeNode<FileNode> = {
          id: currentPath,
          name: part,
          level: index,
          children: [], // Will be populated by Tree component
          isExpanded: true, // Fully expanded by default
          data: isLast ? file : undefined,
          icon: isLast ? (
            <File01Icon size={16} color="var(--colors-fg-muted)" />
          ) : (
            <Folder01Icon size={16} color="var(--colors-fg-muted)" />
          ),
          parent: parentNode, // Set parent reference for Tree component
        };

        pathMap.set(currentPath, node);
        nodes.push(node); // Add to flat array
      }

      parentNode = pathMap.get(currentPath);
    });
  });

  return nodes;
};

export function FileTree({
  files,
  selectedFileId,
  onFileSelect,
}: FileTreeProps) {
  const [selectedNodes, setSelectedNodes] = useState<string[]>([]);
  // Convert files to tree structure
  const treeData = useMemo(() => buildFileTree(files), [files]);

  // Handle selectedFileId prop changes
  useEffect(() => {
    if (selectedFileId && treeData) {
      // Find the node that matches the selectedFileId
      const matchingNode = treeData.find(
        (node) => node.data?.filePath === selectedFileId,
      );

      if (matchingNode) {
        setSelectedNodes([matchingNode.id]);
      }
    } else if (!selectedFileId) {
      setSelectedNodes([]);
    }
  }, [selectedFileId, treeData]);
  // Custom render function for file nodes
  const renderNode = (node: TreeNode<FileNode>) => {
    const isSelected = node.data?.filePath === selectedFileId;

    return (
      <HStack flex="1" gap="2" alignItems="center" minW={0}>
        <Text
          fontSize="xs"
          fontWeight={
            node.data ? (isSelected ? 'semibold' : 'normal') : 'medium'
          }
          color={isSelected ? 'fg.default' : 'fg.muted'}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          title={node.name}
          flex={1}
        >
          {node.name}
        </Text>
      </HStack>
    );
  };

  // Handle selection changes
  const handleSelectionChange = (selectedNodeIds: string[]) => {
    setSelectedNodes(selectedNodeIds);
    // Find the selected file and trigger callback
    const selectedNodeId = selectedNodeIds[selectedNodeIds.length - 1];

    if (selectedNodeId) {
      const selectedNode = treeData.find((node) => node.id === selectedNodeId);

      if (selectedNode?.data) {
        onFileSelect(selectedNode.data);
      }
    }
  };

  return (
    <VStack gap="0" alignItems="stretch" h="full" overflow="hidden" py={1}>
      <Box flex="1" overflowY="auto" overflowX="hidden">
        <Tree
          data={treeData}
          selectedNodes={selectedNodes}
          onSelectionChange={handleSelectionChange}
          fullWidth={true}
          selectionMode="single"
          title=""
          searchPlaceholder="Search files..."
          emptyMessage="No files found"
          renderNode={renderNode}
        />
      </Box>
    </VStack>
  );
}
